import { ref, type Ref } from 'vue';

/** A press held longer than this opens the control UI instead of toggling. */
export const GRID_LONG_PRESS_MS = 400;
/**
 * How far a press may travel and still count as a tap when the finger lifts
 * (CSS px). Deliberately just under the 20px drag the acceptance list calls a
 * pan, and above the 15px drift measured on the tablet: past ~8px the browser
 * has already given up on synthesising a `click`, so the pointer path is the
 * only one left that can still honour the tap.
 */
export const GRID_TAP_SLOP_PX = 16;
/**
 * The browser only cancels our pointer once it has claimed the gesture as a pan
 * (measured: from ~8px of travel). Such a cancel is therefore a scroll, and only
 * a press that never really moved may still be read as a tap.
 */
export const GRID_CANCEL_SLOP_PX = 4;

export interface GridTapHandlers {
  onPointerDown: (e: PointerEvent) => void;
  onPointerMove: (e: PointerEvent) => void;
  onPointerUp: (e: PointerEvent) => void;
  onPointerCancel: (e: PointerEvent) => void;
  onPointerLeave: (e: PointerEvent) => void;
  onClick: (e: MouseEvent) => void;
  didLongPress: Ref<boolean>;
  /**
   * A press is in progress and still counts as one (down, not yet released, and
   * not drifted past `GRID_TAP_SLOP_PX`) — what a cell draws its "holding…"
   * animation off, timed to `GRID_LONG_PRESS_MS`.
   */
  pressing: Ref<boolean>;
}

/**
 * Shared tap / long-press logic for grid widget cells.
 *
 * One press is judged on the pointer stream, not on the browser's `click`:
 * the WebView drops the click as soon as the finger travels a few px and it
 * claims the gesture, so a tap that drifted did nothing at all. Measured on the
 * tablet, card t_fcaa5f33: a 15 CSS px drift on a device tile produced
 * pointercancel (or a bare pointerup) and no click — nothing toggled, nothing
 * scrolled. That is the reported "the first tap did nothing, the second worked".
 *
 * Rules, each with its reason:
 * - `pointerup` toggles when the press travelled no further than
 *   `GRID_TAP_SLOP_PX` and ended before `GRID_LONG_PRESS_MS`.
 * - `pointercancel` toggles only when the press travelled no further than
 *   `GRID_CANCEL_SLOP_PX`: a cancel means the browser claimed a pan, so a press
 *   that moved is a scroll — but a cancel that never moved is an interruption,
 *   and silently dropping it is the lost first tap all over again.
 * - `pointerleave` only aborts the press (mouse: drag off the cell) — never toggles.
 * - the guarded `onClick` keeps keyboard and assistive activation working: it
 *   fires only when the press it belongs to was NOT already toggled on the
 *   pointer path, so a click following a handled tap cannot toggle twice.
 *
 * The host element must declare `touch-action` (`pan-y` inside a vertical
 * scroller) so the browser does not spend the press on its own gesture detection.
 */
export function useGridTap(onTap: () => void, onLongPress?: () => void): GridTapHandlers {
  let timer: ReturnType<typeof setTimeout> | null = null;
  let pointerId: number | null = null;
  let startX = 0;
  let startY = 0;
  let travel = 0;
  let longPressed = false;
  let lastToggledAt = Number.NEGATIVE_INFINITY;
  const didLongPress = ref(false);
  const pressing = ref(false);

  function clearTimer() {
    if (timer !== null) {
      clearTimeout(timer);
      timer = null;
    }
  }

  function toggle() {
    lastToggledAt = performance.now();
    onTap();
  }

  function onPointerDown(e: PointerEvent) {
    if (e.isPrimary === false) return;
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    clearTimer();
    pointerId = e.pointerId;
    startX = e.clientX;
    startY = e.clientY;
    travel = 0;
    longPressed = false;
    didLongPress.value = false;
    pressing.value = true;
    if (!onLongPress) return;
    timer = setTimeout(() => {
      timer = null;
      longPressed = true;
      didLongPress.value = true;
      onLongPress();
    }, GRID_LONG_PRESS_MS);
  }

  function onPointerMove(e: PointerEvent) {
    if (pointerId === null || e.pointerId !== pointerId) return;
    const distance = Math.hypot(e.clientX - startX, e.clientY - startY);
    if (distance > travel) travel = distance;
    if (travel > GRID_TAP_SLOP_PX) {
      clearTimer();
      // A pan is no longer a press: the hold indicator goes with the gesture.
      pressing.value = false;
    }
  }

  function onPointerUp(e: PointerEvent) {
    if (pointerId === null || e.pointerId !== pointerId) return;
    clearTimer();
    pointerId = null;
    pressing.value = false;
    if (longPressed || travel > GRID_TAP_SLOP_PX) return;
    toggle();
  }

  function onPointerCancel(e: PointerEvent) {
    if (pointerId === null || e.pointerId !== pointerId) return;
    clearTimer();
    pointerId = null;
    pressing.value = false;
    if (longPressed || travel > GRID_CANCEL_SLOP_PX) return;
    toggle();
  }

  function onPointerLeave() {
    if (pointerId === null) return;
    clearTimer();
    pointerId = null;
    pressing.value = false;
  }

  function onClick() {
    if (longPressed) {
      longPressed = false;
      return;
    }
    if (performance.now() - lastToggledAt < GRID_LONG_PRESS_MS) return;
    toggle();
  }

  return { onPointerDown, onPointerMove, onPointerUp, onPointerCancel, onPointerLeave, onClick, didLongPress, pressing };
}
